import {
  bigint,
  check,
  doublePrecision,
  foreignKey,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  smallint,
  text,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { orders } from '@trashlab/api/src/domains/orders/schemas/orders';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { communication } from '@trashlab/api/src/domains/comms-center/schemas';
import { isoTimestamp } from '@trashlab/api/src/domains/core/schemas/base-template';

export const order_photos_verification_status = pgEnum(
  'order_photos_verification_status',
  [
    'PENDING', // Photo was uploaded to S3, verification is pending
    'FAILED', // Photo failed to verify in S3
    'VERIFIED', // Photo was verified in S3
  ],
);

export const order_photos_kind = pgEnum('order_photos_kind', [
  'DUMP_TICKET',
  'EXCEPTION',
  'BOX_PICKED_UP',
  'INCOMPLETE_JOB',
  'PROOF_OF_SERVICE',
  'EXTRA_FEE',
  'BOX_IN_YARD',
  'PLACEMENT_INSTRUCTIONS',
  'CUSTOMER_ORDER',
  'VOIDED_INVOICE',
  'TERMS_OF_SERVICE',
  'UNKNOWN',
  'ORDERS_REPORT_EXPORT',
  'REVENUE_REPORT_EXPORT',
  'INVOICE_PDF',
  'DVIR_INSPECTION_ITEM',
  'INSPECTION_PDF',
  'COMMS_MEDIA',
  'COMMENT_ATTACHMENT',
  'MECHANIC_NOTES_ATTACHMENT',
  'UPSELL_ITEM',
  'DUMP_REPORT',
  'CUSTOMER_CREDITS_REPORT',
  'CUSTOMERS_REPORT',
  'ORDERS_REPORT',
  'REVENUE_REPORT',
  'CHARGES_BY_INVOICE_REPORT',
  'CHARGES_BY_BILLING_RUN_REPORT',
  'INVOICE_ATTACHMENT',
  'TAX_REPORT',
  'IMPORT_FILE',
  'ASSET_MOVEMENT',
  'SITESAPP_IMAGE',
  'CUSTOMER_SIGNATURE',
  'SIGNED_CONTRACT_PDF',
  'COMMS_DELIVERY_REPORT',
  'SERVICE_ORDER_EXCEPTION',
  'CALL_EXTENSION_RECORDING',
  'CHANGELOG_IMAGE',
  'USER_PROFILE_IMAGE',
  'SCALE_SNAPSHOT',
  'RENTAL_REVENUE_EXPORT',
  'CUSTOMER_FILE',
  'BROADCAST_ATTACHMENT',
  'QUEUE_MUSIC',
  'QUOTE_ATTACHMENT',
]);

export type OrderPhotosKind =
  | 'UNKNOWN'
  | 'DUMP_TICKET'
  | 'EXCEPTION'
  | 'INCOMPLETE_JOB'
  | 'PROOF_OF_SERVICE'
  | 'EXTRA_FEE'
  | 'BOX_IN_YARD'
  | 'PLACEMENT_INSTRUCTIONS'
  | 'CUSTOMER_ORDER'
  | 'VOIDED_INVOICE'
  | 'BOX_PICKED_UP'
  | 'TERMS_OF_SERVICE'
  | 'ORDERS_REPORT_EXPORT'
  | 'REVENUE_REPORT_EXPORT'
  | 'INVOICE_PDF'
  | 'DVIR_INSPECTION_ITEM'
  | 'INSPECTION_PDF'
  | 'COMMS_MEDIA'
  | 'COMMENT_ATTACHMENT'
  | 'MECHANIC_NOTES_ATTACHMENT'
  | 'UPSELL_ITEM'
  | 'DUMP_REPORT'
  | 'CUSTOMER_CREDITS_REPORT'
  | 'CUSTOMERS_REPORT'
  | 'ORDERS_REPORT'
  | 'REVENUE_REPORT'
  | 'CHARGES_BY_INVOICE_REPORT'
  | 'CHARGES_BY_BILLING_RUN_REPORT'
  | 'INVOICE_ATTACHMENT'
  | 'IMPORT_FILE'
  | 'TAX_REPORT'
  | 'ASSET_MOVEMENT'
  | 'SITESAPP_IMAGE'
  | 'CUSTOMER_SIGNATURE'
  | 'SIGNED_CONTRACT_PDF'
  | 'COMMS_DELIVERY_REPORT'
  | 'CALL_EXTENSION_RECORDING'
  | 'SERVICE_ORDER_EXCEPTION'
  | 'CHANGELOG_IMAGE'
  | 'USER_PROFILE_IMAGE'
  | 'SCALE_SNAPSHOT'
  | 'RENTAL_REVENUE_EXPORT'
  | 'CUSTOMER_FILE'
  | 'BROADCAST_ATTACHMENT'
  | 'QUEUE_MUSIC'
  | 'QUOTE_ATTACHMENT';

export const order_photos = pgTable(
  'order_photos',
  {
    ...defaultFields.fieldsWithTenantId(),
    orderId: uuid('orderId'),
    customerId: uuid('customerId'),
    quoteId: uuid('quoteId'),
    quoteContainerPlacementId: uuid('quoteContainerPlacementId'),
    communicationId: uuid('communicationId'),
    broadcastCampaignId: uuid('broadcastCampaignId'),
    s3Path: varchar('s3Path').notNull(),
    kind: order_photos_kind('kind').notNull(),
    fileType: varchar('fileType').notNull(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    notes: text('notes').default('').notNull(),
    displayName: varchar('displayName', { length: 255 }),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    signedUrl: varchar('signedUrl'),
    signedUrlAtTs: isoTimestamp('signedUrlAtTs', { withTimezone: true }),
    verificationStatus: order_photos_verification_status('verificationStatus'),
    s3VerifiedAt: isoTimestamp('s3VerifiedAt', { withTimezone: true }),
    uploadedBy: uuid('uploadedBy'),
    sourceProvider: varchar('sourceProvider', { length: 64 }),
    sourceExternalId: varchar('sourceExternalId', { length: 255 }),
    sourceRequestId: varchar('sourceRequestId', { length: 255 }),
    sourceAssetId: uuid('sourceAssetId'),
    parentOrderPhotoId: uuid('parentOrderPhotoId'),
    capturedAt: isoTimestamp('capturedAt', { withTimezone: true }),
    durationMs: integer('durationMs'),
    trimStartMs: integer('trimStartMs'),
    trimEndMs: integer('trimEndMs'),
    cameraPosition: varchar('cameraPosition', { length: 64 }),
    sourceLatitude: doublePrecision('sourceLatitude'),
    sourceLongitude: doublePrecision('sourceLongitude'),
    sourceDistanceMeters: doublePrecision('sourceDistanceMeters'),
    mediaMetadata: jsonb('mediaMetadata').default({}).notNull(),
    capturedAtTs: isoTimestamp('capturedAtTs', { withTimezone: true }),
    capturedUtcOffsetMinutes: smallint('capturedUtcOffsetMinutes'),
    capturedLatitude: doublePrecision('capturedLatitude'),
    capturedLongitude: doublePrecision('capturedLongitude'),
    locationFixAtTs: isoTimestamp('locationFixAtTs', { withTimezone: true }),
    captureMetadataStatus: varchar('captureMetadataStatus'),
    captureMetadataProcessedAtTs: isoTimestamp('captureMetadataProcessedAtTs', {
      withTimezone: true,
    }),
    stampLastAttemptAtTs: isoTimestamp('stampLastAttemptAtTs', {
      withTimezone: true,
    }),
    stampedAtTs: isoTimestamp('stampedAtTs', { withTimezone: true }),
    verifiedContentDigest: varchar('verifiedContentDigest', { length: 64 }),
    customerSiteId: uuid('customerSiteId'),
    uploadedByNameSnapshot: varchar('uploadedByNameSnapshot', { length: 255 }),
  },
  (table) => {
    return {
      order_photos_captured_utc_offset_minutes_check: check(
        'order_photos_captured_utc_offset_minutes_check',
        sql`${table.capturedUtcOffsetMinutes} BETWEEN -840 AND 840`,
      ),
      createdAtTs_idx: index('order_photos_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      order_id_idx: index('order_photos_order_id_idx').on(
        table.tenantId,
        table.orderId,
      ),
      customer_id_idx: index('order_photos_customer_id_idx').on(
        table.tenantId,
        table.customerId,
      ),
      customer_site_id_idx: index('order_photos_customer_site_id_idx').on(
        table.tenantId,
        table.customerSiteId,
      ),
      quote_id_idx: index('order_photos_quote_id_idx').on(
        table.tenantId,
        table.quoteId,
      ),
      communication_id_idx: index('order_photos_communication_id_idx').on(
        table.tenantId,
        table.communicationId,
      ),
      broadcast_campaign_active_idx: index(
        'order_photos_broadcast_campaign_active_idx',
      )
        .on(table.broadcastCampaignId, table.kind, table.createdAtTs, table.id)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.broadcastCampaignId} IS NOT NULL`,
        ),
      verification_queue_idx: index('order_photos_verification_queue_idx')
        .on(
          table.tenantId,
          table.verificationStatus,
          table.uploadedBy,
          table.updatedAtTs.desc(),
          table.id,
        )
        .where(sql`${table.archivedAtTs} IS NULL`),
      pending_verification_reconciliation_idx: index(
        'order_photos_pending_verification_reconciliation_idx',
      )
        .on(table.updatedAtTs, table.id)
        .where(
          sql`${table.verificationStatus} = 'PENDING' AND ${table.archivedAtTs} IS NULL`,
        ),
      tenantId_idx: index('order_photos_tenantId_idx').on(table.tenantId),
      tenant_kind_created_at_idx: index(
        'order_photos_tenant_kind_created_at_idx',
      ).on(table.tenantId, table.kind, table.createdAtTs.desc()),
      source_request_idx: index('order_photos_source_request_idx').on(
        table.tenantId,
        table.sourceProvider,
        table.sourceRequestId,
      ),
      source_external_idx: index('order_photos_source_external_idx').on(
        table.tenantId,
        table.sourceProvider,
        table.sourceExternalId,
      ),
      exception_snapshot_source_uidx: uniqueIndex(
        'order_photos_exception_snapshot_source_uidx',
      )
        .on(table.tenantId, table.sourceExternalId)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.kind} = 'SERVICE_ORDER_EXCEPTION' AND ${table.sourceProvider} = 'MOTIVE' AND ${table.sourceExternalId} LIKE 'exception-snapshot:%'`,
        ),
      source_asset_idx: index('order_photos_source_asset_idx').on(
        table.tenantId,
        table.sourceAssetId,
      ),
      motive_asset_order_uidx: uniqueIndex(
        'order_photos_motive_asset_order_uidx',
      )
        .on(table.tenantId, table.orderId, table.sourceAssetId)
        .where(
          sql`${table.sourceAssetId} IS NOT NULL AND ${table.parentOrderPhotoId} IS NULL AND ${table.archivedAtTs} IS NULL`,
        ),
      parent_order_photo_idx: index('order_photos_parent_order_photo_idx').on(
        table.tenantId,
        table.parentOrderPhotoId,
      ),
      updatedAtTs_idx: index('order_photos_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('order_photos_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('order_photos_versionNumber_idx').on(
        table.versionNumber,
      ),

      //foreign keys
      order_photos_orderId_fkey: foreignKey({
        columns: [table.orderId],
        foreignColumns: [orders.id],
        name: 'order_photos_orderId_fkey',
      }),
      order_photos_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'order_photos_customerSiteId_fkey',
      }),
      order_photos_parentOrderPhotoId_fkey: foreignKey({
        columns: [table.parentOrderPhotoId],
        foreignColumns: [table.id],
        name: 'order_photos_parentOrderPhotoId_fkey',
      }),
      // Note: broadcastCampaignId and quoteId foreign key constraints omitted
      // to avoid circular dependency. Constraints exist in the database via migration.
      // Relationship will be enforced at application level
    };
  },
);

export const order_photos_history = pgTable(
  'order_photos_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    orderId: uuid('orderId'),
    customerId: uuid('customerId'),
    quoteId: uuid('quoteId'),
    quoteContainerPlacementId: uuid('quoteContainerPlacementId'),
    communicationId: uuid('communicationId'),
    broadcastCampaignId: uuid('broadcastCampaignId'),
    s3Path: varchar('s3Path'),
    kind: order_photos_kind('kind'),
    fileType: varchar('fileType'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    notes: text('notes'),
    displayName: varchar('displayName', { length: 255 }),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    signedUrl: varchar('signedUrl'),
    signedUrlAtTs: isoTimestamp('signedUrlAtTs', { withTimezone: true }),
    verificationStatus: order_photos_verification_status('verificationStatus'),
    s3VerifiedAt: isoTimestamp('s3VerifiedAt', { withTimezone: true }),
    uploadedBy: uuid('uploadedBy'),
    sourceProvider: varchar('sourceProvider', { length: 64 }),
    sourceExternalId: varchar('sourceExternalId', { length: 255 }),
    sourceRequestId: varchar('sourceRequestId', { length: 255 }),
    sourceAssetId: uuid('sourceAssetId'),
    parentOrderPhotoId: uuid('parentOrderPhotoId'),
    capturedAt: isoTimestamp('capturedAt', { withTimezone: true }),
    durationMs: integer('durationMs'),
    trimStartMs: integer('trimStartMs'),
    trimEndMs: integer('trimEndMs'),
    cameraPosition: varchar('cameraPosition', { length: 64 }),
    sourceLatitude: doublePrecision('sourceLatitude'),
    sourceLongitude: doublePrecision('sourceLongitude'),
    sourceDistanceMeters: doublePrecision('sourceDistanceMeters'),
    mediaMetadata: jsonb('mediaMetadata'),
    capturedAtTs: isoTimestamp('capturedAtTs', { withTimezone: true }),
    capturedUtcOffsetMinutes: smallint('capturedUtcOffsetMinutes'),
    capturedLatitude: doublePrecision('capturedLatitude'),
    capturedLongitude: doublePrecision('capturedLongitude'),
    locationFixAtTs: isoTimestamp('locationFixAtTs', { withTimezone: true }),
    captureMetadataStatus: varchar('captureMetadataStatus'),
    captureMetadataProcessedAtTs: isoTimestamp('captureMetadataProcessedAtTs', {
      withTimezone: true,
    }),
    stampLastAttemptAtTs: isoTimestamp('stampLastAttemptAtTs', {
      withTimezone: true,
    }),
    stampedAtTs: isoTimestamp('stampedAtTs', { withTimezone: true }),
    verifiedContentDigest: varchar('verifiedContentDigest', { length: 64 }),
    customerSiteId: uuid('customerSiteId'),
    uploadedByNameSnapshot: varchar('uploadedByNameSnapshot', { length: 255 }),
  },
  (table) => ({
    order_photos_history_captured_utc_offset_minutes_check: check(
      'order_photos_history_captured_utc_offset_minutes_check',
      sql`${table.capturedUtcOffsetMinutes} BETWEEN -840 AND 840`,
    ),
  }),
);
